import { defineStore } from 'pinia';
import { ref } from 'vue';
import { customAxiosInstance } from '@/common/api/utils/mutator';
import { useInternational } from '@/common/locale';

/**
 * 챗봇 환경설정/사용량 store.
 *
 * 책임:
 *   - GET /api/v9/chatbot/settings → enabled/provider/model/disabledTools 캐싱
 *   - PUT /api/v9/chatbot/settings → master 전용 변경 + 응답으로 캐시 갱신
 *   - GET /api/v9/chatbot/providers → provider×model + tool list (UI drop-down 용)
 *   - GET /api/v9/chatbot/usage/{series,by-user,summary} → 사용량 데이터
 *
 * 모든 endpoint 는 `/api` 경유 → gateway → API. JWT 는 axios interceptor 가 자동 첨부.
 */

export interface ChatbotSettings {
  enabled: boolean;
  provider: string;
  model: string;
  disabledTools: string[];
  /** provider 별 저장된 키의 마스킹된 표시값 ("****abcd"). 평문은 절대 안 옴. */
  providerKeysMasked?: Record<string, string> | null;
  /** provider 별 endpoint (ollama / vllm 만 의미). */
  providerEndpoints?: Record<string, string> | null;
  /** provider 별 기억된 model — 종류 전환 시 복원용. */
  providerModels?: Record<string, string> | null;
  /** 봇 응답 메타 표시 항목 on/off — {showModel, showTokens, showElapsed}. 기본 모두 on. */
  responseMetaOptions?: Record<string, boolean> | null;
  /** 응답 생성 중 도구 호출 이벤트(tool_call/tool_result) 스트리밍 — 칩 표시 on/off. */
  streamToolEvents?: boolean;
  /** 분석/RCA 답변을 항상 구조화 카드로 강제(백엔드 최종 콜). on 시 일관성↑·지연↑. 기본 on. */
  forcedStructuredAnswer?: boolean;
  /** 의도 기반 도구선언 서브셋팅 — 이동/조회 의도에 도구 노출 축소(입력 토큰↓). 기본 on. */
  intentToolSubsetting?: boolean;
  /** 멀티라운드 도구결과 압축 — 오래된 큰 결과 배열 축소(토큰↓·citation 보존). 기본 on. */
  compactOldToolResults?: boolean;
  /** Gemini 컨텍스트 캐시 TTL(초). 기본 3600. */
  geminiCacheTtlSeconds?: number;
  /** 도구결과 캐시 글로벌 TTL(초). 0 = per-tool 기본값. */
  toolCacheTtlSeconds?: number;
  /** 알람 상세 'AI 분석' 탭 모드 — 'off'(탭 숨김) | 'manual'(버튼) | 'auto'(탭 진입 시 자동). 기본 manual. */
  alarmDetailRcaMode?: string;
  /** Ollama 컨텍스트 윈도우(num_ctx). 기본 49152. (cloud 호스팅 모델은 서버 관리로 미적용 가능) */
  ollamaNumCtx?: number;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface ChatbotSettingsUpdate {
  enabled?: boolean;
  provider?: string;
  model?: string;
  disabledTools?: string[];
  /** 평문 키 — undefined/null = 변경 없음, "" = 키 제거 (env fallback). */
  providerKeyUpdates?: Record<string, string>;
  /** ollama / vllm 의 endpoint URL — undefined = 변경 없음. */
  providerEndpoints?: Record<string, string>;
  /** provider 별 model — undefined = 변경 없음, 보낸 키만 부분 갱신. */
  providerModelUpdates?: Record<string, string>;
  /** 응답 메타 표시 항목 — undefined = 변경 없음, 보낸 키만 부분 갱신. */
  responseMetaOptions?: Record<string, boolean>;
  /** 도구 호출 이벤트 스트리밍 — undefined = 변경 없음. */
  streamToolEvents?: boolean;
  /** 구조화 카드 강제 — undefined = 변경 없음. */
  forcedStructuredAnswer?: boolean;
  /** 의도 도구 서브셋팅 — undefined = 변경 없음. */
  intentToolSubsetting?: boolean;
  /** 도구결과 압축 — undefined = 변경 없음. */
  compactOldToolResults?: boolean;
  /** Gemini 캐시 TTL(초) — undefined = 변경 없음. */
  geminiCacheTtlSeconds?: number;
  /** 도구결과 캐시 TTL(초) — undefined = 변경 없음. */
  toolCacheTtlSeconds?: number;
  /** 알람 상세 AI 분석 모드 (off|manual|auto) — undefined = 변경 없음. */
  alarmDetailRcaMode?: string;
  /** Ollama num_ctx (컨텍스트 윈도우) — undefined = 변경 없음. 기본 49152. */
  ollamaNumCtx?: number;
}

export interface ProviderEntry {
  id: string;
  label: string;
  apiKeySet: boolean;
  models: string[];
}

export interface ToolEntry {
  name: string;
  description: string;
  enabled: boolean;
}

export interface ProvidersResponse {
  providers: ProviderEntry[];
  tools: ToolEntry[];
}

export interface UsageBucket {
  label: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  callCount: number;
  errorCount: number;
  /** 조회 시점 계산 비용(USD) — 백엔드 LlmPricingTable 기반. 가격표에 없는 모델(로컬 LLM)은 0. */
  costUsd: number;
}

export type UsageGranularity = '10m' | '1h' | '1d' | '1mo';
/**
 * 호출 출처 필터 — undefined/'' = 전체, 'user' = 사용자 입력,
 * 'proactive' = 내부시스템(선제 RCA), 'alarm_rca' = 알람 상세 RCA. (proactive·alarm_rca = 내부시스템)
 */
export type UsageSource = 'user' | 'proactive' | 'alarm_rca';

export interface UsageSeriesResponse {
  userId: string;
  from: string;
  to: string;
  granularity: UsageGranularity;
  buckets: UsageBucket[];
  totals: UsageBucket;
}

export interface UsageByUserResponse {
  from: string;
  to: string;
  users: UsageBucket[];
  totals: UsageBucket;
}

/** 차원별(모델별 / 출처별) 합계 응답 — /usage/by-model, /usage/by-source 공용. */
export interface UsageBreakdownResponse {
  dimension: 'model' | 'source';
  from: string;
  to: string;
  rows: UsageBucket[];
  totals: UsageBucket;
}

export interface UsageSummaryResponse {
  today: UsageBucket;
  last7: UsageBucket;
  last30: UsageBucket;
}

export interface ChatbotSpecialist {
  id: number;
  specialistKey: string;
  name: string;
  emoji?: string | null;
  description?: string | null;
  /** 콤마 구분 문자열 (예: "was,트랜잭션,jvm"). */
  keywords?: string | null;
  prompt: string;
  priority: number;
  enabled: boolean;
  isMaster: boolean;
  isDefault: boolean;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

/** POST 는 specialistKey/name/prompt 필수, PUT 은 보낸 필드만 부분 업데이트. */
export interface ChatbotSpecialistPayload {
  specialistKey?: string;
  name?: string;
  emoji?: string;
  description?: string;
  keywords?: string;
  prompt?: string;
  priority?: number;
  enabled?: boolean;
}

interface ChatbotSpecialistsResponse {
  count: number;
  specialists: ChatbotSpecialist[];
}

/**
 * provider ping 결과. 백엔드 ping 엔드포인트가 아직 미구현이어도
 * UI 가 graceful degrade 할 수 있도록 'unsupported' 상태를 표준화.
 */
export type ProviderPingResult =
  | { status: 'success'; latencyMs?: number; model?: string }
  | { status: 'failure'; httpStatus?: number; message: string }
  | { status: 'unsupported' }; // 404 = 백엔드 후속

export const useChatbotConfigStore = defineStore('chatbotConfig', () => {
  const { t } = useInternational();

  // 설정
  const settings = ref<ChatbotSettings | null>(null);
  const settingsLoading = ref(false);
  const settingsSaving = ref(false);
  const settingsError = ref<string | null>(null);

  const fetchSettings = async (): Promise<void> => {
    settingsLoading.value = true;
    settingsError.value = null;
    try {
      const res = await customAxiosInstance<ChatbotSettings>({
        url: '/api/v9/chatbot/settings',
        method: 'GET',
      });
      settings.value = res.data;
    } catch (e) {
      settingsError.value = t('WORD.CHATBOT_CONFIG.SETTINGS_LOAD_ERROR');
      // 실서버 값과 구분 불가한 placeholder 를 만들면, 관리자가 무관한 항목 하나만 고쳐 저장해도
      // 가짜 enabled/provider/model 이 실제 설정을 덮어쓸 수 있다 — 실패 시 settings 는 비워 둔다
      // (SettingsTab 은 settings 부재 시 draft 세팅·저장을 모두 차단).
      console.warn('[chatbot-config] fetchSettings failed', e);
    } finally {
      settingsLoading.value = false;
    }
  };

  // 저장(PUT) 오류는 조회(GET) 유효성 오류와 분리 — settingsError 를 공유하면 canSave 가
  // (조회 실패 가드로) 이를 요구해, 저장 한 번 실패 후 재시도 버튼이 영구 비활성화된다 (재리뷰 P2)
  const settingsSaveError = ref<string | null>(null);

  const updateSettings = async (patch: ChatbotSettingsUpdate): Promise<boolean> => {
    settingsSaving.value = true;
    settingsSaveError.value = null;
    try {
      const res = await customAxiosInstance<ChatbotSettings>({
        url: '/api/v9/chatbot/settings',
        method: 'PUT',
        data: patch,
      });
      settings.value = res.data;
      return true;
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 403) settingsSaveError.value = t('WORD.CHATBOT_CONFIG.NEED_ADMIN');
      else if (status === 400) settingsSaveError.value = t('WORD.CHATBOT_CONFIG.INVALID_INPUT');
      else settingsSaveError.value = t('WORD.CHATBOT_CONFIG.SAVE_FAIL');
      return false;
    } finally {
      settingsSaving.value = false;
    }
  };

  // provider × model + tool 카탈로그
  const catalog = ref<ProvidersResponse | null>(null);
  const fetchCatalog = async (): Promise<void> => {
    try {
      const res = await customAxiosInstance<ProvidersResponse>({
        url: '/api/v9/chatbot/providers',
        method: 'GET',
      });
      catalog.value = res.data;
    } catch (e) {
      console.warn('[chatbot-config] fetchCatalog failed', e);
    }
  };

  /**
   * provider 연결 테스트. 저장된 키를 그대로 사용하거나(SaaS), 입력 중인 새 키/endpoint 로
   * lightweight ping 한다. 404 면 백엔드 미구현 → 'unsupported' 로 변환해 UI 가 friendly 표시 가능.
   */
  const pingProvider = async (
    providerId: string,
    overrides?: { apiKey?: string; endpoint?: string; model?: string },
  ): Promise<ProviderPingResult> => {
    try {
      const res = await customAxiosInstance<{ ok: boolean; latencyMs?: number; model?: string }>({
        url: `/api/v9/chatbot/providers/${encodeURIComponent(providerId)}/ping`,
        method: 'POST',
        // 빈 바디(undefined)면 axios 가 Content-Type 을 안 붙여 서버 @RequestBody(JSON) 가 415 를 반환함.
        // override 가 없어도 빈 객체를 보내 Content-Type: application/json 을 강제한다.
        data: overrides && Object.keys(overrides).length > 0 ? overrides : {},
      });
      if (res.data?.ok) {
        return { status: 'success', latencyMs: res.data.latencyMs, model: res.data.model };
      }
      return { status: 'failure', message: t('WORD.CHATBOT_CONFIG.PING_FAIL') };
    } catch (e: any) {
      const httpStatus = e?.response?.status;
      if (httpStatus === 404) return { status: 'unsupported' };
      const detail =
        e?.response?.data?.error || e?.message || t('WORD.CHATBOT_CONFIG.UNKNOWN_ERROR');
      return { status: 'failure', httpStatus, message: detail };
    }
  };

  // 에이전트 (분석 전문가)
  const specialists = ref<ChatbotSpecialist[]>([]);

  const fetchSpecialists = async (): Promise<void> => {
    try {
      const res = await customAxiosInstance<ChatbotSpecialistsResponse>({
        url: '/api/v9/chatbot/specialists',
        method: 'GET',
      });
      specialists.value = res.data?.specialists || [];
    } catch (e) {
      console.warn('[chatbot-config] fetchSpecialists failed', e);
      throw e;
    }
  };

  const createSpecialist = async (payload: ChatbotSpecialistPayload): Promise<void> => {
    try {
      await customAxiosInstance({
        url: '/api/v9/chatbot/specialists',
        method: 'POST',
        data: payload,
      });
    } catch (e) {
      console.warn('[chatbot-config] createSpecialist failed', e);
      throw e;
    }
  };

  const updateSpecialist = async (id: number, payload: ChatbotSpecialistPayload): Promise<void> => {
    try {
      await customAxiosInstance({
        url: `/api/v9/chatbot/specialists/${id}`,
        method: 'PUT',
        data: payload,
      });
    } catch (e) {
      console.warn('[chatbot-config] updateSpecialist failed', e);
      throw e;
    }
  };

  const removeSpecialist = async (id: number): Promise<void> => {
    try {
      await customAxiosInstance({
        url: `/api/v9/chatbot/specialists/${id}`,
        method: 'DELETE',
      });
    } catch (e) {
      console.warn('[chatbot-config] removeSpecialist failed', e);
      throw e;
    }
  };

  // 사용량
  const summary = ref<UsageSummaryResponse | null>(null);
  const series = ref<UsageSeriesResponse | null>(null);
  const byUser = ref<UsageByUserResponse | null>(null);
  const byModel = ref<UsageBreakdownResponse | null>(null);
  const bySource = ref<UsageBreakdownResponse | null>(null);
  /** 사용량 새로고침 진행 여부 — 사용량 탭의 refresh() 가 전체 요청 묶음 단위로 관리. */
  const usageLoading = ref(false);
  /** 사용량 조회 실패 안내 (취소 제외) — null 이면 정상. */
  const usageError = ref<string | null>(null);

  /** 공통 쿼리 파라미터 빌더 — 빈 값은 제외(서버 default 적용). */
  const usageParams = (p: {
    userId?: string;
    from?: string;
    to?: string;
    granularity?: UsageGranularity;
    source?: UsageSource;
  }): Record<string, string> => {
    const out: Record<string, string> = {};
    if (p.userId) out.userId = p.userId;
    if (p.from) out.from = p.from;
    if (p.to) out.to = p.to;
    if (p.granularity) out.granularity = p.granularity;
    if (p.source) out.source = p.source;
    return out;
  };

  /** axios 요청 취소(AbortController.abort) 에러 여부 — out-of-order 가드에서 stale 응답을 무시. */
  const isCanceled = (e: unknown): boolean =>
    (e as { code?: string })?.code === 'ERR_CANCELED' ||
    (e as { name?: string })?.name === 'CanceledError';

  const fetchSummary = async (
    userId?: string,
    source?: UsageSource,
    signal?: AbortSignal,
  ): Promise<void> => {
    try {
      const res = await customAxiosInstance<UsageSummaryResponse>({
        url: '/api/v9/chatbot/usage/summary',
        method: 'GET',
        params: usageParams({ userId, source }),
        signal,
      });
      summary.value = res.data;
    } catch (e) {
      if (isCanceled(e)) return;
      usageError.value = t('WORD.CHATBOT_CONFIG.USAGE_LOAD_ERROR');
      console.warn('[chatbot-config] fetchSummary failed', e);
    }
  };

  const fetchSeries = async (
    params: {
      userId?: string;
      from: string;
      to: string;
      granularity?: UsageGranularity;
      source?: UsageSource;
    },
    signal?: AbortSignal,
  ): Promise<void> => {
    try {
      const res = await customAxiosInstance<UsageSeriesResponse>({
        url: '/api/v9/chatbot/usage/series',
        method: 'GET',
        params: usageParams(params),
        signal,
      });
      series.value = res.data;
    } catch (e) {
      if (isCanceled(e)) return;
      usageError.value = t('WORD.CHATBOT_CONFIG.USAGE_LOAD_ERROR');
      console.warn('[chatbot-config] fetchSeries failed', e);
    }
  };

  const fetchByUser = async (
    params: {
      from: string;
      to: string;
      source?: UsageSource;
    },
    signal?: AbortSignal,
  ): Promise<void> => {
    try {
      const res = await customAxiosInstance<UsageByUserResponse>({
        url: '/api/v9/chatbot/usage/by-user',
        method: 'GET',
        params: usageParams(params),
        signal,
      });
      byUser.value = res.data;
    } catch (e: any) {
      if (isCanceled(e)) return; // stale 응답 — 최신 데이터 보존(null 로 덮지 않음)
      if (e?.response?.status !== 403) {
        usageError.value = t('WORD.CHATBOT_CONFIG.USAGE_LOAD_ERROR');
        console.warn('[chatbot-config] fetchByUser failed', e);
      }
      // 403 = master 아님 → 조용히 무시
      byUser.value = null;
    }
  };

  const fetchByModel = async (
    params: {
      userId?: string;
      from: string;
      to: string;
      source?: UsageSource;
    },
    signal?: AbortSignal,
  ): Promise<void> => {
    try {
      const res = await customAxiosInstance<UsageBreakdownResponse>({
        url: '/api/v9/chatbot/usage/by-model',
        method: 'GET',
        params: usageParams(params),
        signal,
      });
      byModel.value = res.data;
    } catch (e) {
      if (isCanceled(e)) return;
      usageError.value = t('WORD.CHATBOT_CONFIG.USAGE_LOAD_ERROR');
      console.warn('[chatbot-config] fetchByModel failed', e);
      byModel.value = null;
    }
  };

  const fetchBySource = async (
    params: {
      userId?: string;
      from: string;
      to: string;
    },
    signal?: AbortSignal,
  ): Promise<void> => {
    try {
      const res = await customAxiosInstance<UsageBreakdownResponse>({
        url: '/api/v9/chatbot/usage/by-source',
        method: 'GET',
        params: usageParams(params),
        signal,
      });
      bySource.value = res.data;
    } catch (e) {
      if (isCanceled(e)) return;
      usageError.value = t('WORD.CHATBOT_CONFIG.USAGE_LOAD_ERROR');
      console.warn('[chatbot-config] fetchBySource failed', e);
      bySource.value = null;
    }
  };

  return {
    settings,
    settingsLoading,
    settingsSaving,
    settingsError,
    settingsSaveError,
    fetchSettings,
    updateSettings,
    catalog,
    fetchCatalog,
    pingProvider,
    specialists,
    fetchSpecialists,
    createSpecialist,
    updateSpecialist,
    removeSpecialist,
    summary,
    series,
    byUser,
    byModel,
    bySource,
    usageLoading,
    usageError,
    fetchSummary,
    fetchSeries,
    fetchByUser,
    fetchByModel,
    fetchBySource,
  };
});
